/**
 * CommunityChat.jsx
 * Chat communauté Body-Up — ultra pro, ultra motivant.
 * Accessible aux membres coaching (49 CHF/mois+).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send, Lock, Heart, ThumbsUp, Flame, Dumbbell,
  Reply, X, Loader2, Users, Crown, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { useClientAuth } from '../../context/ClientAuthContext';

const EMOJIS = ['👍', '❤️', '🔥', '💪'];
const EMOJI_ICONS = { '👍': ThumbsUp, '❤️': Heart, '🔥': Flame, '💪': Dumbbell };
const POLL_MS = 5000;

const PLAN_LABELS = {
  communaute: 'Communauté',
  transformation: 'Transformation',
  vip: 'VIP Hybrid',
};

const avatarColors = [
  'from-pink-500 to-rose-600',
  'from-violet-500 to-purple-600',
  'from-cyan-500 to-blue-600',
  'from-emerald-500 to-teal-600',
  'from-orange-500 to-amber-600',
  'from-fuchsia-500 to-pink-600',
];

function getColor(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) & 0xffffffff;
  return avatarColors[Math.abs(h) % avatarColors.length];
}

function Avatar({ name, initials, isCoach, size = 9 }) {
  if (isCoach) {
    return (
      <div className={`w-${size} h-${size} rounded-full flex items-center justify-center shrink-0 bg-gradient-to-br from-primary to-pink-500 ring-2 ring-primary/50`}>
        <Crown className="w-4 h-4 text-white" />
      </div>
    );
  }
  const colors = getColor(name);
  return (
    <div className={`w-${size} h-${size} rounded-full flex items-center justify-center shrink-0 bg-gradient-to-br ${colors} text-white text-xs font-bold`}>
      {initials || (name?.charAt(0) || '?').toUpperCase()}
    </div>
  );
}

function MessageBubble({ msg, me, onReact, onReply }) {
  const isMine = msg.email === me;
  const [showEmojis, setShowEmojis] = useState(false);

  const handleReact = (emoji) => {
    onReact(msg.message_id, emoji);
    setShowEmojis(false);
  };

  const reactions = msg.reactions || {};
  const hasReactions = Object.keys(reactions).length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={`flex items-end gap-2 group ${isMine ? 'flex-row-reverse' : 'flex-row'}`}
      data-testid={`community-msg-${msg.message_id?.slice(0,8)}`}
    >
      {!isMine && (
        <div className="shrink-0 mb-1">
          <Avatar name={msg.name} initials={msg.initials} isCoach={msg.is_coach} />
        </div>
      )}

      <div className={`max-w-[75%] sm:max-w-[60%] ${isMine ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
        {/* Nom + badge */}
        {!isMine && (
          <div className="flex items-center gap-1.5 px-1">
            <span className="text-xs font-semibold text-zinc-300">{msg.name}</span>
            {msg.is_coach && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-gradient-to-r from-primary/20 to-pink-500/20 text-primary border border-primary/30">
                <Crown className="w-2.5 h-2.5" /> Coach
              </span>
            )}
          </div>
        )}

        {/* Citation */}
        {msg.reply_to && (
          <div className={`text-xs px-3 py-1.5 rounded-xl border border-white/5 bg-zinc-800/80 text-zinc-400 max-w-full truncate ${isMine ? 'self-end' : 'self-start'}`}>
            <span className="text-zinc-500">↩ {msg.reply_to.name} : </span>
            {msg.reply_to.text}
          </div>
        )}

        {/* Bulle du message */}
        <div className="relative">
          <div
            className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed cursor-pointer
              ${isMine
                ? 'bg-gradient-to-br from-primary/80 to-pink-600/80 text-white rounded-br-sm'
                : msg.is_coach
                  ? 'bg-gradient-to-br from-purple-900/60 to-zinc-800 text-white border border-purple-500/30 rounded-bl-sm'
                  : 'bg-zinc-800/90 text-zinc-100 rounded-bl-sm border border-white/5'
              }`}
            onClick={() => setShowEmojis(v => !v)}
          >
            {msg.text}
          </div>

          {/* Picker emoji */}
          <AnimatePresence>
            {showEmojis && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: 4 }}
                className={`absolute bottom-full mb-1 z-20 flex gap-1 bg-zinc-900 border border-white/10 rounded-2xl px-2 py-1.5 shadow-xl ${isMine ? 'right-0' : 'left-0'}`}
              >
                {EMOJIS.map(emoji => (
                  <button
                    key={emoji}
                    onClick={() => handleReact(emoji)}
                    className="text-lg hover:scale-125 transition-transform px-1"
                    title={`Réagir avec ${emoji}`}
                  >
                    {emoji}
                  </button>
                ))}
                <button onClick={() => { onReply(msg); setShowEmojis(false); }}
                  className="text-zinc-400 hover:text-white transition-colors pl-1 border-l border-white/10 ml-1"
                  title="Répondre">
                  <Reply className="w-3.5 h-3.5" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Réactions existantes */}
        {hasReactions && (
          <div className={`flex gap-1 flex-wrap ${isMine ? 'justify-end' : 'justify-start'}`}>
            {Object.entries(reactions).map(([emoji, users]) =>
              users.length > 0 ? (
                <button
                  key={emoji}
                  onClick={() => onReact(msg.message_id, emoji)}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-all
                    ${users.includes(me) ? 'border-primary/50 bg-primary/15 text-primary' : 'border-white/10 bg-zinc-800/70 text-zinc-300 hover:border-white/20'}`}
                >
                  <span>{emoji}</span>
                  <span className="font-semibold">{users.length}</span>
                </button>
              ) : null
            )}
          </div>
        )}

        {/* Heure */}
        <span className="text-[10px] text-zinc-600 px-1">
          {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>

      {/* Boutons d'action au survol */}
      <div className={`opacity-0 group-hover:opacity-100 transition-opacity self-center flex gap-1 ${isMine ? 'flex-row-reverse' : ''}`}>
        <button onClick={() => onReply(msg)} className="text-zinc-500 hover:text-zinc-300 p-1" title="Répondre">
          <Reply className="w-3.5 h-3.5" />
        </button>
      </div>
    </motion.div>
  );
}

function DateSeparator({ date }) {
  const d = new Date(date);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  const label = isToday ? "Aujourd'hui" : d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  return (
    <div className="flex items-center gap-3 my-4">
      <div className="flex-1 h-px bg-white/5" />
      <span className="text-[10px] text-zinc-500 uppercase tracking-widest font-mono">{label}</span>
      <div className="flex-1 h-px bg-white/5" />
    </div>
  );
}

export default function CommunityChat({ en = false }) {
  const L = (fr, eng) => en ? eng : fr;
  const { authFetch } = useClientAuth();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [locked, setLocked] = useState(false);
  const [me, setMe] = useState('');
  const [meName, setMeName] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const lastCreatedAt = useRef(null);
  const pollRef = useRef(null);

  const scrollToBottom = useCallback((smooth = true) => {
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  const fetchMessages = useCallback(async (opts = {}) => {
    try {
      const res = await authFetch(`/api/community/messages?limit=40`);
      if (res.status === 402) { setLocked(true); setLoading(false); return; }
      if (!res.ok) throw new Error();
      const data = await res.json();
      setMe(data.me || '');
      setMeName(data.me_name || '');
      setTotal(data.total || 0);
      const newMsgs = data.messages || [];
      if (!opts.poll) {
        setMessages(newMsgs);
        setHasMore((data.total || 0) > newMsgs.length);
        setTimeout(() => scrollToBottom(false), 50);
      } else {
        // Polling : ajouter seulement les nouveaux messages
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.message_id));
          const added = newMsgs.filter(m => !existingIds.has(m.message_id));
          if (added.length === 0) return prev;
          setTimeout(() => scrollToBottom(true), 50);
          return [...prev, ...added];
        });
      }
    } catch {
      // silently fail on poll
    } finally {
      setLoading(false);
    }
  }, [scrollToBottom]);

  useEffect(() => {
    fetchMessages();
    pollRef.current = setInterval(() => fetchMessages({ poll: true }), POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchMessages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setInput('');
    try {
      const res = await authFetch(`/api/community/messages`, {
        method: 'POST',
        body: JSON.stringify({ text, reply_to: replyTo?.message_id || null }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur envoi message');
        setInput(text);
        return;
      }
      const data = await res.json();
      setMessages(prev => [...prev, data.message]);
      setReplyTo(null);
      setTimeout(() => scrollToBottom(true), 50);
    } catch {
      toast.error('Erreur réseau');
      setInput(text);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleReact = async (messageId, emoji) => {
    try {
      const res = await authFetch(`/api/community/messages/${messageId}/react`, {
        method: 'POST',
        body: JSON.stringify({ emoji }),
      });
      if (!res.ok) return;
      const data = await res.json();
      setMessages(prev => prev.map(m =>
        m.message_id === messageId ? { ...m, reactions: data.reactions } : m
      ));
    } catch {}
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Grouper les messages par date pour les séparateurs
  const grouped = [];
  let lastDate = null;
  for (const msg of messages) {
    const d = msg.created_at?.slice(0, 10);
    if (d && d !== lastDate) {
      grouped.push({ type: 'separator', date: msg.created_at });
      lastDate = d;
    }
    grouped.push({ type: 'message', msg });
  }

  // ── Locked state ──────────────────────────────────────────────────────────
  if (locked) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4 text-center px-6">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-purple-500/20 border border-primary/30 flex items-center justify-center">
          <Lock className="w-7 h-7 text-primary" />
        </div>
        <div>
          <h3 className="font-bold text-white text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {L('Communauté réservée', 'Members only')}
          </h3>
          <p className="text-zinc-400 text-sm mt-1 max-w-xs mx-auto">
            {L('Accessible à partir du plan Communauté (49 CHF/mois). Rejoignez-nous !', 'Available from the Community plan (49 CHF/month). Join us!')}
          </p>
        </div>
      </div>
    );
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[600px] max-h-[75vh]" data-testid="community-chat">
      {/* Header */}
      <div className="shrink-0 px-4 py-3 border-b border-white/8 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center">
          <Users className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-bold text-white text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {L('Communauté Body-Up', 'Body-Up Community')}
          </h3>
          <p className="text-[10px] text-zinc-500">{total} {L('messages · Coaching & entraide', 'messages · Coaching & support')}</p>
        </div>
        <div className="ml-auto">
          <span className="flex items-center gap-1 text-[10px] text-emerald-400">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse inline-block" />
            {L('En direct', 'Live')}
          </span>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2 scrollbar-thin scrollbar-thumb-zinc-700" data-testid="community-messages">
        {messages.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
            <Sparkles className="w-10 h-10 text-primary/40" />
            <div>
              <p className="text-zinc-400 font-semibold">{L('Soyez le premier à écrire !', 'Be the first to write!')}</p>
              <p className="text-zinc-600 text-sm mt-1">{L('Partagez votre progression, posez vos questions...', 'Share your progress, ask questions...')}</p>
            </div>
          </div>
        )}

        {grouped.map((item, idx) =>
          item.type === 'separator'
            ? <DateSeparator key={`sep-${idx}`} date={item.date} />
            : <MessageBubble
                key={item.msg.message_id}
                msg={item.msg}
                me={me}
                onReact={handleReact}
                onReply={(msg) => { setReplyTo(msg); inputRef.current?.focus(); }}
              />
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Zone de saisie */}
      <div className="shrink-0 border-t border-white/8 bg-zinc-950/80 backdrop-blur-sm px-3 py-3">
        {/* Citation en cours */}
        <AnimatePresence>
          {replyTo && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="flex items-start justify-between gap-2 bg-zinc-800/80 rounded-xl px-3 py-2 mb-2 border-l-2 border-primary"
            >
              <div className="text-xs text-zinc-400 truncate">
                <span className="text-primary font-semibold">{replyTo.name} </span>
                {replyTo.text?.slice(0, 60)}
              </div>
              <button onClick={() => setReplyTo(null)} className="text-zinc-500 hover:text-white shrink-0">
                <X className="w-3.5 h-3.5" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex items-end gap-2">
          <Avatar name={meName} initials={meName?.charAt(0)?.toUpperCase() || '?'} isCoach={false} size={8} />
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder={L('Écrire un message… (Entrée pour envoyer)', 'Write a message… (Enter to send)')}
            className="flex-1 bg-zinc-800/80 border border-white/10 rounded-2xl px-4 py-2.5 text-white text-sm placeholder-zinc-500 focus:border-primary focus:outline-none resize-none min-h-[44px] max-h-[120px]"
            rows={1}
            maxLength={1000}
            data-testid="community-input"
          />
          <button
            onClick={handleSend}
            disabled={sending || !input.trim()}
            className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-r from-primary to-pink-500 text-white flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="community-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
